import * as Sentry from "@sentry/react";
import axios, { Axios, isAxiosError, type CreateAxiosDefaults } from "axios";
import createAuthRefresh from "axios-auth-refresh";

import { currentPlatform, platformApi } from "@/app/platform";
import { growthbook } from "@/lib/growthbook";
import { CommonTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import type {
  GetUserDetailsOutputDto,
  RefreshTokenOutputDto,
} from "@/types/codegen";
import { isProduction, isTest } from "@/utils/constants";
import { allowPurchaseExpiresAtStorage } from "@/utils/localStorage";
import {
  accessTokenStorage,
  decode,
  refreshTokenStorage,
} from "@/utils/tokens";

export const BASE_API_URL = `${import.meta.env.VITE_BASE_API_URL}/v1`;
export const BASE_API_URL_V2 = `${import.meta.env.VITE_BASE_API_URL}/v2`;
export const BASE_API_URL_V3 = `${import.meta.env.VITE_BASE_API_URL}/v3`;

const config: CreateAxiosDefaults = {
  baseURL: BASE_API_URL,
  timeout: 60 * 1000, // 60 seconds - to match CloudFront's 60s timeout
};

export const api = axios.create(config);
export const unauthenticatedApi = axios.create(config);

export async function authenticate() {
  const getTokenData = async () => {
    const existingToken = accessTokenStorage.get();

    if (existingToken) {
      return {
        accessToken: existingToken,
        source: "storage",
      } as const;
    }

    const { accessToken, refreshToken } = await platformApi.signIn();

    accessTokenStorage.set(accessToken);
    refreshTokenStorage.set(refreshToken);

    return { accessToken, source: "sign_in" } as const;
  };

  const getUserDetails = async () => {
    try {
      // until BE fixes the inconsistent response shape for this endpoint
      const { data } = await api.get<
        GetUserDetailsOutputDto & {
          data: {
            userDetails: {
              userId: string;
              name: string | undefined;
              phoneNumber: string | undefined;
            };
          };
        }
      >("/user");
      return data.data.userDetails;
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const { accessToken, source } = await getTokenData();

  const { userId } = decode(accessToken);

  Sentry.setUser({ id: userId });
  growthbook.setAttributes({ id: userId });

  const userDetails = await getUserDetails();

  rudderAnalytics.identify(userId, {
    phone: userDetails?.phoneNumber,
  });

  if (source === "sign_in") {
    rudderAnalytics.track(CommonTrackingEvents.USER_LOGGED_IN, {
      login_method: currentPlatform,
    });
  }

  return accessToken;
}

type RequestInterceptorParams = Parameters<
  Axios["interceptors"]["request"]["use"]
>;
type ResponseInterceptorParams = Parameters<
  Axios["interceptors"]["response"]["use"]
>;

const addAuthHeadersInterceptor: RequestInterceptorParams[0] = (config) => {
  const token = accessTokenStorage.get();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
};

const addCustomHeadersInterceptor: RequestInterceptorParams[0] = (config) => {
  config.headers["X-Timestamp"] = new Date().toISOString();
  return config;
};

const blockOrdersIfPurchaseNotAllowed: RequestInterceptorParams[0] = (
  config,
) => {
  if (
    config.url &&
    ["/orders", "/orders/package"].includes(config.url) &&
    config.method === "post" &&
    !isProduction &&
    !Number(allowPurchaseExpiresAtStorage.get())
  ) {
    return Promise.reject(new axios.Cancel("Purchase blocked"));
  }
  return config;
};

const onResponseSuccess: ResponseInterceptorParams[0] = (response) => response;

const refreshTokens = async () => {
  const oldRefreshToken = refreshTokenStorage.get();
  if (!oldRefreshToken) {
    throw new Error("No refresh token available");
  }

  const response = await unauthenticatedApi.post<RefreshTokenOutputDto>(
    "/auth/tokens/refresh",
    {
      refreshToken: oldRefreshToken,
    },
  );

  const { accessToken, refreshToken } = response.data.data;
  accessTokenStorage.set(accessToken);
  refreshTokenStorage.set(refreshToken);
  return accessToken;
};

const refreshAuthLogic = (failedRequest: unknown) => {
  if (!isAxiosError(failedRequest)) {
    return Promise.reject(failedRequest);
  }

  const result = refreshTokens()
    .then((tokenRefreshResponse) => {
      if (failedRequest.response) {
        failedRequest.response.config.headers["Authorization"] =
          `Bearer ${tokenRefreshResponse}`;
      }
      return Promise.resolve();
    })
    .catch(() => {
      accessTokenStorage.remove();
      refreshTokenStorage.remove();

      // if refresh failed, try to re-authenticate the user
      const result = authenticate()
        .then((accessToken) => {
          if (failedRequest.response) {
            failedRequest.response.config.headers["Authorization"] =
              `Bearer ${accessToken}`;
          }
          return Promise.resolve();
        })
        .catch((error) => {
          return Promise.reject(error);
        });

      return result;
    });

  return result;
};

createAuthRefresh(api, refreshAuthLogic);

const logErrorInterceptor: ResponseInterceptorParams[1] = async (error) => {
  if (axios.isCancel(error) || (isAxiosError(error) && error.status === 401)) {
    return Promise.reject(error);
  }

  if (!isProduction && !isTest) {
    console.error(
      isAxiosError(error)
        ? `API ERROR: [${error.config?.method}] ${error.config?.url} ${error.response ? error.response.status : "Unknown"}`
        : `API ERROR: ${error?.message}`,
    );
  }

  Sentry.captureException(error);

  return Promise.reject(error);
};

api.interceptors.request.use(addAuthHeadersInterceptor);
api.interceptors.request.use(addCustomHeadersInterceptor);
api.interceptors.response.use(onResponseSuccess, logErrorInterceptor);

if (!isProduction) {
  api.interceptors.request.use(blockOrdersIfPurchaseNotAllowed);
}

unauthenticatedApi.interceptors.request.use(addCustomHeadersInterceptor);
unauthenticatedApi.interceptors.response.use(
  onResponseSuccess,
  logErrorInterceptor,
);
